// Лента трендов на витрине (section_type='trend_feed') — «место» единого
// пула карточек: сама лента карточек не хранит, участники отмечают её в
// feeds[] (админ: модал карточки → «Размещение → Ленты» / значок мест).
// Виды блока (config.layout):
//   'wide' 16:10 (кураторский тег, название + подзаголовок капсом) · 'reels' 9:16 (градиентный бейдж, название) · 'slider' — крупные
//   9:16 в горизонтальной прокрутке · 'grid' — плитки с лимитом visibleCount
//   и аккордеоном «Показать другие тренды» · 'vlist' — вертикальный стек
//   широких · 'hero' — одна крупная карточка «Тренд недели» с CTA.
// theme='event' — событийная подложка-тинт (например «ЧМ 2026»).
// Видео-превью карточки играет на ховере (как у грид-карточек), клик отдаёт
// карточку наверх — хост ведёт штатный флоу (пин → виджет с этапами).
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import SliderRow from './SliderRow'
import CardMediaBar from './CardMediaBar'
import { claimTileSound, onTileSoundClaimed, releaseTileSound, tileSoundToken } from '../utils/tileSound'
import { activeDiscount, trendDiscount, type TrendPipePricing } from '../utils/discount'
import { trendsLabel } from '../utils/pluralRu'
import type { CardKind } from '../types/generationCard'
import styles from './TrendFeedSection.module.scss'
import { cardAutoplayAllowed, useCardVideoAutoplay } from '../utils/cardVideoAutoplay'
import { buildTileVideoQueue, EMPTY_TILE_QUEUE, type TileVideoMember } from '../utils/tileVideoQueue'
import { useTileVideoQueue } from '../hooks/useTileVideoQueue'
import TileVideoWarm from './TileVideoWarm'
import { useNearViewport } from '../utils/nearViewport'
import { contrastVars, normHex } from '../utils/feedTheme'
import { FlowLinkIcon } from '../icons/FlowLink.tsx'
import { Coin } from '../icons/Coin.tsx'
import PencilIcon from '../icons/PencilIcon'
import CardImageLightbox, { type LightboxImage } from './CardImageLightbox'
import PreviewImg from './PreviewImg'
import { sectionPreviewUrl } from '../utils/sectionPreview'
import DropletGlass from './DropletGlass'

export interface TrendFeedCardData {
	id?: string
	/** Слаг лендинга (у героя-участника мульти-тренда — чтобы найти его группу). */
	slug?: string
	title?: string
	description?: string
	background_url?: string
	/** Лёгкое webp-превью (серверная конвенция `<key>_preview.webp`) — плитки
	 *  грузят его, onError-фолбэк на оригинал. Hero грузит оригинал. */
	background_preview_url?: string | null
	video_url?: string
	/** Постер видео — его первый кадр (`<key>_preview.webp` от upload-video).
	 *  Работает для карточек БЕЗ обложки: плитка перестаёт зависеть от того,
	 *  решит браузер декодировать кадр или нет. Едет атрибутом poster, так что
	 *  отсутствующий файл ничего не ломает. */
	video_preview_url?: string | null
	/** Лёгкий ролик-сосед для автоплея плитки (бэк отдаёт, только если объект есть). */
	video_loop_url?: string | null
	/** Крупный сосед `<key>_loop_xl.mp4` — им играет блок с крупными карточками
	 *  (обычный 960 на такой плитке растянулся бы до 132 %). Бэк отдаёт поле
	 *  только страницам, где такой блок есть. */
	video_loop_xl_url?: string | null
	badge_text?: string
	badge_style?: string
	/** Мультистиль (владелец 2026-08-16): универсальная карточка — в лентах
	 *  плитка тоже несёт бейдж «N стилей» и свотчи стилей над заголовком;
	 *  без баннера обложкой служит первый стиль. */
	card_kind?: CardKind
	/** Мульти-тренд (22.10): участники группы — бейдж «N трендов» и свотчи их кадров над названием. */
	members?: Array<{
		id: string
		title?: string
		background_url?: string
		background_preview_url?: string | null
		video_preview_url?: string | null
		/** Ролики версий — очередь роликов плитки (utils/tileVideoQueue). */
		video_url?: string | null
		video_loop_url?: string | null
		video_loop_xl_url?: string | null
	}>
	/** Карточка из шаблона флоу: сводка с бэка — шаги и цена всего результата. */
	flow_template_id?: string
	flow?: { steps: number; price_from: number | null } | null
	demo_variants?: Array<{ key: string; label?: string; after_url?: string; is_open?: boolean }>
	/** 'droplet' — плитка «стекло-капля» (рефракция кромки + тень, DropletGlass). */
	tile_style?: string
	/** Размеры ролика и обложки (пишет модал карточки из ответа аплоада, старым —
	 *  бэкфилл backfill_card_media_dims): по ним комбинированная лента выбирает
	 *  форму плитки. Нет размеров — плитка вертикальная, как формат блока. */
	video_w?: number | null
	video_h?: number | null
	cover_w?: number | null
	cover_h?: number | null
	/** Форма плитки в комбинированной ленте, заданная админом: reels | wide | square;
	 *  пусто — по размерам медиа. */
	tile_format?: string | null
	/** Плашка «Это ты» — по карточке (галочка в админке), в любом формате. */
	you_badge?: boolean
	pipeline_slug?: string
	/** Акция: у тренда — из этапов (pipeline), у обычной — price_override. */
	pipeline?: TrendPipePricing
	price_override?: number
	discount_price?: number
	discount_until?: string
}

/** Формат КАРТОЧЕК блока — ортогонален раскладке: 'reels' 9:16 всем, 'wide' 16:10 всем,
 *  'mixed' — комбинированный: форма каждой плитки по её ролику (владелец 16.09), высота
 *  ряда и размер — по настройкам блока. */
export type TrendFeedTile = 'reels' | 'wide' | 'mixed'
/** Форма ОДНОЙ плитки комбинированной ленты. */
export type TrendCardShape = 'reels' | 'wide' | 'square'

/** Форма плитки по карточке: явный tile_format админа, иначе пропорция ролика (а без
 *  ролика — обложки): шире 1,15 — горизонтальная 16:9, уже 0,87 — вертикальная 9:16,
 *  между — квадрат. Размеров нет — вертикальная. */
// eslint-disable-next-line react-refresh/only-export-components
export function cardShape(c: TrendFeedCardData): TrendCardShape {
	const forced = String(c.tile_format || '')
	if (forced === 'reels' || forced === 'wide' || forced === 'square') return forced
	const [w, h] = c.video_url && c.video_w && c.video_h
		? [Number(c.video_w), Number(c.video_h)]
		: c.cover_w && c.cover_h
			? [Number(c.cover_w), Number(c.cover_h)]
			: [0, 0]
	if (!(w > 0 && h > 0)) return 'reels'
	const r = w / h
	return r > 1.15 ? 'wide' : r < 0.87 ? 'reels' : 'square'
}
/** РАСКЛАДКА блока — как карточки расставлены. */
export type TrendFeedArrangement = 'row' | 'slider' | 'grid' | 'vlist' | 'hero'
/** Легаси-значения config.layout ('reels'|'wide') = формат + раскладка 'row'. */
export type TrendFeedLayout = TrendFeedTile | TrendFeedArrangement

/** Нормализация конфига блока: старые 'reels'/'wide' в layout задают формат
 *  при раскладке «лента»; новые конфиги несут tile и layout раздельно. */
// eslint-disable-next-line react-refresh/only-export-components
export function normalizeTrendFeedView(
	layout?: unknown,
	tile?: unknown,
): { tile: TrendFeedTile; arrangement: TrendFeedArrangement } {
	const rawLayout = String(layout || 'reels')
	const legacy = rawLayout === 'reels' || rawLayout === 'wide'
	const t = String(tile || (legacy ? rawLayout : 'reels'))
	const arr = legacy ? 'row' : rawLayout
	return {
		tile: t === 'wide' ? 'wide' : t === 'mixed' ? 'mixed' : 'reels',
		arrangement: (['row', 'slider', 'grid', 'vlist', 'hero'] as const).includes(
			arr as TrendFeedArrangement,
		)
			? (arr as TrendFeedArrangement)
			: 'row',
	}
}

/** Свой порядок карточек ленты (админ: перетаскивание плиток в блоке →
 *  config.card_order). Массив id: перечисленные идут заданным порядком,
 *  остальные — следом, в порядке пула; так только что добавленная в ленту
 *  карточка не теряется и не ломает раскладку. Порядок СВОЙ у каждой ленты:
 *  одна и та же карточка живёт в нескольких лентах со своим местом в каждой.
 *  Применяется только в режиме «Как в ленте» — «по популярности» и
 *  «перемешивать» задают порядок сами. */
// eslint-disable-next-line react-refresh/only-export-components
export function applyFeedCardOrder<T extends { id?: string }>(cards: T[], order: unknown): T[] {
	if (!Array.isArray(order) || order.length === 0) return cards
	const rank = new Map<string, number>()
	order.forEach((id, i) => {
		if (typeof id === 'string' && !rank.has(id)) rank.set(id, i)
	})
	// Стабильно: незнакомые id сохраняют относительный порядок пула и уходят в конец.
	return cards
		.map((c, i) => ({ c, i, r: rank.get(String(c.id ?? '')) ?? Number.POSITIVE_INFINITY }))
		.sort((a, b) => a.r - b.r || a.i - b.i)
		.map((x) => x.c)
}

/** Сетка ленты «во всю ширину»: шире блок — больше плиток в ряду, а не крупнее плитка. Колонок —
 *  столько, чтобы плитка вышла ближе всего к своей ширине в обычной ленте (cardW), и не меньше, чем в
 *  обычной ленте (min): на узком экране широкий блок выглядит как обычный. */
// eslint-disable-next-line react-refresh/only-export-components
export function fitGridCols(width: number, cardW: number, gap: number, min: number): number {
	if (!(width > 0) || !(cardW > 0)) return Math.max(1, min)
	return Math.max(1, min, Math.round((width + gap) / (cardW + gap)))
}

/** Что плитка показывает ДО наведения: обложка (лёгкое превью, иначе оригинал)
 *  или постер видео. Пусто — показывать нечего, и первый кадр приходится
 *  добывать предзагрузкой самого видео. */
const stillOf = (c: TrendFeedCardData): string | undefined =>
	c.background_preview_url || c.background_url || mstyCoverOf(c) || c.video_preview_url || undefined

/** Стили мультистиль-карточки (обычные, с фото). */
const mstyStylesOf = (c: TrendFeedCardData) =>
	c.card_kind === 'multistyle'
		? (c.demo_variants || []).filter((v) => !v.is_open && v.after_url)
		: []

/** «Свой вариант» мультистиля (владелец 2026-08-21) — карандаш в хвосте ряда. */
const mstyOpenOf = (c: TrendFeedCardData) =>
	c.card_kind === 'multistyle' ? (c.demo_variants || []).filter((v) => v.is_open) : []

/** Участники мульти-тренда (кадр свотча: превью обложки, иначе постер ролика). */
const trendMembersOf = (c: TrendFeedCardData) =>
	c.card_kind === 'multitrend'
		? (c.members || []).filter((m) => !!m?.id).map((m) => ({
			id: m.id,
			title: m.title || '',
			src: m.background_preview_url || m.background_url || m.video_preview_url || '',
			videoUrl: m.video_url || undefined,
			videoLoopUrl: m.video_loop_url || undefined,
			videoLoopXlUrl: m.video_loop_xl_url || undefined,
			videoPosterUrl: m.video_preview_url || undefined,
		}))
		: []

/** Обложка-фолбэк мультистиля: первый стиль (явный баннер приоритетен). */
const mstyCoverOf = (c: TrendFeedCardData): string | undefined =>
	c.background_url ? undefined : mstyStylesOf(c)[0]?.after_url

// Устройство без ховера (телефон/планшет): ховер-превью видео не запускаем.
const TOUCH_DEVICE = typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches


interface Props {
	title: string
	subtitle?: string
	icon?: string
	/** Раскладка (или легаси 'reels'|'wide' — см. normalizeTrendFeedView). */
	layout: string
	/** Формат карточек; пусто = 'reels' (или формат из легаси-layout). */
	tile?: string
	cards: TrendFeedCardData[]
	onPick: (card: TrendFeedCardData, index: number) => void
	/** Мульти-тренд (23.10): клик по кадру версии на плитке — открыть группу сразу с этой версией.
	 *  Без колбэка ряд декоративный. */
	onPickMember?: (card: TrendFeedCardData, memberId: string, index: number) => void
	/** Ссылка лендинга карточки: плитка рендерится <a href> (SEO-перелинковка,
	 *  средняя кнопка), обычный клик перехватывается в onPick (SPA-переход). */
	cardHref?: (card: TrendFeedCardData) => string | undefined
	/** grid: сколько плиток видно до «Показать другие тренды» (деф. 10).
	 *  Легаси-величина: блоки до появления рядов хранят только её. */
	visibleCount?: number
	/** grid: сколько РЯДОВ видно до «Показать другие тренды» (0 — не задано,
	 *  считаем по visibleCount). Ряд — единственная величина, одинаковая на
	 *  любой ширине: колонок на телефоне и на столе разное число. */
	visibleRows?: number
	/** Размер карточек блока: 'lg' — крупные (в ~1,35 раза шире обычных),
	 *  'sm' — компактные (в ~0,8). Пусто/'md' — обычный размер. */
	cardSize?: string
	/** Легаси 'event' — событийный тинт (новые конфиги несут bgColor). */
	theme?: string
	/** HEX-подложка блока (#rrggbb/#rgb); пусто = без подложки. Текст блока
	 *  автоконтрастится под цвет (светлый фон → тёмный текст и наоборот). */
	bgColor?: string
	/** hero: карточка блока (резолв сервера); нет — берётся первая из cards. */
	heroCard?: TrendFeedCardData | null
	/** Действие на ховере (настройка блока `hover_action`): под названием раскрывается описание, а в
	 *  правом нижнем углу появляется стрелка «перейти». Текст промпта при этом не показывается: промпт
	 *  карточки скрыт, роль подсказки играет описание. На тач-устройствах ховера нет — плитка как была. */
	hoverAction?: boolean
	/** Блок главной «во всю ширину» (config.width === 'wide'): лента без колонки 1200px. Размер плиток не
	 *  меняется — в ряд встаёт больше плиток (у сетки широких колонки считает fitGridCols). */
	fullWidth?: boolean
}


const BADGE_CLASS: Record<string, string> = {
	viral: styles.badgeViral,
	hit: styles.badgeHit,
	cringe: styles.badgeCringe,
}

/**
 * Видео плитки ленты: при автоплее им управляет менеджер (играет то, что видно,
 * не больше лимита), иначе — прежнее поведение «играем на наведении».
 *
 * src появляется только вместе с игрой: пока его нет, браузер не тянет ни байта
 * и рисует постер, а снятие src — единственный способ вернуть постер после
 * первого play() (спека <video>) и отдать декодер уехавшей за экран плитке.
 */
function FeedVideo({ src, loopSrc, poster, overlay, soundOn = false, onSoundLost, members, tileId, onPlayingMember }: {
	src: string
	/** Лёгкий сосед `<key>_loop.mp4` — им и играем, если бэк его сделал. */
	loopSrc?: string
	poster?: string
	overlay: boolean
	/** Мульти-тренд: версии с роликами — плитка играет их по очереди (utils/tileVideoQueue). */
	members?: TileVideoMember[]
	tileId?: string
	/** Чей ролик очереди сейчас играет (подсветка кадра версии); null — очереди нет. */
	onPlayingMember?: (id: string | null) => void
	/** Звук включён кнопкой бара: играем ОРИГИНАЛ (сосед снят без дорожки) и
	 *  без mute. Включение — жест человека, поэтому браузер такой play() пускает. */
	soundOn?: boolean
	/** Плитка перестала играть (уехала за экран, менеджер снял src) — звук
	 *  гаснет: заново без жеста незамьюченный play() браузер не даст. */
	onSoundLost?: () => void
}) {
	const ref = useRef<HTMLVideoElement>(null)
	// Обложку ставим, когда плитка рядом с экраном (utils/nearViewport): poster браузер качает сразу у всех.
	const near = useNearViewport(ref, !!poster)
	// Автоплей — только если бэк сделал лёгкий ролик: иначе плитка тянула бы
	// оригинал 720p+ (замер: 7 роликов в кадре = 24 МБ). Нет соседа — прежний ховер.
	const [managed] = useState(() => Boolean(loopSrc) && cardAutoplayAllowed())
	const [hover, setHover] = useState(false)
	const { active: auto, setHovered } = useCardVideoAutoplay(ref, managed)
	const live = managed ? auto : hover
	// Очередь роликов версий (23.10): состав — utils/tileVideoQueue, ход и прогрев следующего — hooks/useTileVideoQueue;
	// без версий очередь пуста и всё как раньше.
	const queue = useMemo(
		() => (members?.length ? buildTileVideoQueue({ id: tileId ?? '', src, loopSrc, poster }, members) : EMPTY_TILE_QUEUE),
		[members, tileId, src, loopSrc, poster],
	)
	const vq = useTileVideoQueue(ref, queue)
	useEffect(() => {
		onPlayingMember?.(vq.playingId)
	}, [vq.playingId]) // eslint-disable-line react-hooks/exhaustive-deps
	const cur = vq.queued ? vq.entry : null
	const playSrc = soundOn ? (cur?.src ?? src) : cur ? cur.loopSrc || cur.src : loopSrc || src
	// На смене ролика очереди — постер этой версии; первому ролику постер плитки, как раньше.
	const posterNow = cur && !vq.first ? cur.poster || poster : poster

	// Смена ролика (лёгкий ↔ оригинал) перезагружает элемент — запускаем заново
	// В ОБЕ стороны (иначе после выключения звука плитка замирала на паузе);
	// плитка замолкла не по кнопке — сообщаем наверх, чтобы бар не врал.
	useEffect(() => {
		if (live) void ref.current?.play().catch(() => {})
		else if (soundOn) onSoundLost?.()
	}, [soundOn, live, onSoundLost])
	// Показать до старта нечего — плиткой служит само видео: src держим всегда.
	const videoIsTile = !poster
	// Ховер-лифт плитки (.card:hover — translateY+scale) даёт дребезг
	// enter/leave: замер показал три пары событий за 1.6 с. Перематываем не
	// чаще раза в 600 мс, иначе ролик дёргался бы на месте.
	const lastRewind = useRef(0)

	// Без автоплея src появляется вместе с ховером — play() зовём после рендера.
	useEffect(() => {
		if (managed || !hover) return
		void ref.current?.play().catch(() => {})
	}, [managed, hover])

	const onEnter = () => {
		const v = ref.current
		const now = performance.now()
		if (v?.currentSrc && now - lastRewind.current > 600) {
			lastRewind.current = now
			v.currentTime = 0
		}
		if (managed) setHovered(true)
		else setHover(true)
	}
	const onLeave = () => {
		// Под автоплеем уход курсора ничего не останавливает: плитка остаётся
		// в наборе (менеджер держит её форой) и играет дальше.
		if (managed) setHovered(false)
		else setHover(false)
	}

	return (
		<>
		<video
			ref={ref}
			className={`${styles.media}${overlay ? ` ${styles.videoOverlay}` : ''}${live ? ` ${styles.videoLive}` : ''}`}
			src={managed && !videoIsTile ? (live ? playSrc : undefined) : playSrc}
			/* Обложка — пока плитка далеко от экрана, её нет (под видео видна ленивая картинка-обложка или фон). */
			poster={near || live ? posterNow : undefined}
			muted={!soundOn}
			/* Очередь версий: без loop, по концу ролика — следующий (useTileVideoQueue). */
			loop={!vq.queued}
			playsInline
			onEnded={vq.onEnded}
			onTimeUpdate={vq.onTimeUpdate}
			onPause={vq.onStop}
			onEmptied={vq.onStop}
			data-autoplay={managed ? '1' : undefined}
			/* Наведение = смотреть с начала (владелец 12.09): перематываем даже
			   играющую плитку. Уход курсора ролик не останавливает. */
			onMouseEnter={TOUCH_DEVICE ? undefined : onEnter}
			onMouseLeave={TOUCH_DEVICE ? undefined : onLeave}
			/* Есть что показать до старта (обложка или постер) — видео НЕ трогаем
			   до play(). `preload=metadata` звучит скромно, но браузер тянет
			   начало файла мегабайтами: замер на 6.3МБ-ролике дал 5.3МБ ещё до
			   того, как его хоть кто-то увидел. Показывать пока нечего —
			   оставляем metadata, иначе плитка будет пустой. */
			preload={videoIsTile ? 'metadata' : 'none'}
			/* Битое/протухшее видео — убираем слой, чтобы осталась обложка. Но
			   НЕ когда src просто снят (плитка ушла с экрана): load() без
			   источника тоже стреляет error, а слой обязан вернуться. */
			onError={(e) => { if (e.currentTarget.getAttribute('src')) e.currentTarget.style.display = 'none' }}
		/>
		<TileVideoWarm entry={vq.warmEntry} soundOn={soundOn} />
		</>
	)
}

const TILE_CLASS: Record<TrendFeedTile, string> = {
	reels: styles.tileReels,
	wide: styles.tileWide,
	mixed: styles.tileMixed,
}

/** Класс формы плитки в комбинированной ленте (вертикальная — без класса). */
const SHAPE_CLASS: Record<TrendCardShape, string> = {
	reels: '',
	wide: styles.cardWide,
	square: styles.cardSquare,
}

const ARR_CLASS: Record<TrendFeedArrangement, string> = {
	row: styles.feedRow,
	slider: styles.feedSlider,
	grid: styles.feedGrid,
	vlist: styles.feedVlist,
	hero: styles.feedHero,
}

export default function TrendFeedSection({
	title,
	subtitle,
	icon,
	layout,
	tile: tileProp,
	cards,
	onPick,
	onPickMember,
	visibleCount = 10,
	visibleRows = 0,
	cardSize,
	theme,
	bgColor,
	heroCard,
	cardHref,
	hoverAction = false,
	fullWidth = false,
}: Props) {
	// Лайтбокс «глазика» (владелец 2026-08-16): полноразмерный просмотр
	// картинки тренда; мультистиль — пилюли стилей.
	const [lightboxImages, setLightboxImages] = useState<LightboxImage[] | null>(null)
	const openLightbox = (c: TrendFeedCardData) => {
		const mstyles = mstyStylesOf(c)
		// Полноразмер по запросу: оригинал обложки, превью — только фолбэк.
		const cover = c.background_url || stillOf(c)
		setLightboxImages(
			mstyles.length
				? mstyles.map((v) => ({ url: v.after_url as string, label: v.label }))
				// Видео-карточка ленты: открываем ролик, обложка — его постер.
				: c.video_url
					? [{ url: cover || '', label: c.title, video: c.video_url }]
					: cover
						? [{ url: cover, label: c.title }]
						: null,
		)
	}
	const rowRef = useRef<HTMLDivElement>(null)
	// Звук на плитке: одна на секцию, а через tileSound — одна на страницу.
	const [soundCard, setSoundCard] = useState<string | null>(null)
	const [soundToken] = useState(tileSoundToken)
	useEffect(() => onTileSoundClaimed(soundToken, () => setSoundCard(null)), [soundToken])
	const toggleSound = (id: string) => {
		if (soundCard === id) {
			setSoundCard(null)
			releaseTileSound()
		} else {
			setSoundCard(id)
			claimTileSound(soundToken)
		}
	}
	const soundLost = useCallback((id: string) => setSoundCard((cur) => (cur === id ? null : cur)), [])
	// Число колонок сетки: читаем у живого grid-контейнера (CSS auto-fill не
	// сообщает его иначе) и пересчитываем на ресайзе.
	const [gridCols, setGridCols] = useState(0)
	// Лента «во всю ширину», сетка широких плиток: колонок — ближайшее к ширине плитки обычной ленты
	// (fitGridCols). Число ставится на всю секцию: хвост под «Показать ещё» — своя сетка с теми же колонками.
	const [fitCols, setFitCols] = useState<number | null>(null)
	useLayoutEffect(() => {
		const el = rowRef.current
		if (!el) return
		const read = () => {
			const cs = getComputedStyle(el)
			// Ширину плитки обычной ленты и её число колонок дают стили (.feedFull в модуле) — только там,
			// где сетке нужен подсчёт; в остальных раскладках, на телефоне и в обычной ленте их нет.
			const cardW = parseFloat(cs.getPropertyValue('--tf-grid-card-w'))
			if (cardW > 0) {
				const n = fitGridCols(el.clientWidth, cardW, parseFloat(cs.columnGap) || 0, parseInt(cs.getPropertyValue('--tf-grid-base-cols'), 10) || 1)
				setFitCols(n)
				setGridCols(n)
				return
			}
			setFitCols(null)
			setGridCols(cs.gridTemplateColumns.split(' ').filter(Boolean).length)
		}
		read()
		const ro = new ResizeObserver(read)
		ro.observe(el)
		return () => ro.disconnect()
	}, [])
	// grid: раскрыт ли хвост за пределами visibleCount. tailMounted — хвост
	// монтируется ЛЕНИВО при первом раскрытии (свёрнутые плитки не тянут
	// превью/метаданные видео), после — остаётся в DOM ради анимации сворачивания.
	const [expanded, setExpanded] = useState(false)
	const [tailMounted, setTailMounted] = useState(false)

	const { tile, arrangement } = normalizeTrendFeedView(layout, tileProp)
	// Крупные карточки — отдельный класс размера (ширину плитки задаёт ОДНА
	// переменная --tf-card-w, см. модуль стилей) и отдельный ролик: обычный
	// сосед снят под 960, на крупной плитке его растянуло бы до 132 %.
	const big = cardSize === 'lg'
	const sizeCls = big ? styles.sizeLg : cardSize === 'sm' ? styles.sizeSm : ''
	const mixed = tile === 'mixed'
	// Горизонтальная плитка комбинированной ленты — во всю высоту ряда, втрое шире
	// вертикальной: ей, как и крупным карточкам, нужен крупный сосед.
	const loopOf = (c: TrendFeedCardData): string | undefined =>
		(big || (mixed && cardShape(c) === 'wide') ? c.video_loop_xl_url || c.video_loop_url : c.video_loop_url) || undefined
	// Ключ звучащей плитки: id карточки, а у легаси-карточек без id — сам ролик.
	const soundIdOf = (c: TrendFeedCardData): string => c.id || c.video_url || ''
	// Версии группы для очереди роликов плитки: лёгкий сосед — тот же размер, что у самой плитки (крупной — xl).
	const queueMembersOf = (c: TrendFeedCardData): TileVideoMember[] =>
		trendMembersOf(c).map((m) => ({
			id: m.id,
			videoUrl: m.videoUrl,
			videoLoopUrl: big || (mixed && cardShape(c) === 'wide') ? m.videoLoopXlUrl || m.videoLoopUrl : m.videoLoopUrl,
			videoPosterUrl: m.videoPosterUrl,
		}))
	// Чей ролик очереди сейчас на плитке (ключ — как у звука): кадр этой версии с ободком.
	const [playingMembers, setPlayingMembers] = useState<Record<string, string | null>>({})
	const setPlayingMember = useCallback((key: string, id: string | null) => {
		setPlayingMembers((prev) => (prev[key] === id ? prev : { ...prev, [key]: id }))
	}, [])

	// Плитку под автоплеем эти обработчики НЕ трогают (data-autoplay): иначе уход
	// мыши ставил бы на паузу и перематывал в начало ролик, который играет сам.
	const playOn = (e: React.MouseEvent) => {
		// Тач: mouseenter приходит перед тапом (mouseleave — никогда) — видео
		// играло бы невидимым под обложкой (слой открывается только @media
		// hover:hover), впустую тратя сеть и батарею.
		if (TOUCH_DEVICE) return
		const v = e.currentTarget.querySelector('video')
		if (v && v.dataset.autoplay !== '1') void v.play().catch(() => {})
	}
	const playOff = (e: React.MouseEvent) => {
		const v = e.currentTarget.querySelector('video')
		if (v && v.dataset.autoplay !== '1') {
			v.pause()
			v.currentTime = 0
		}
	}

	// «Широкий» вид плитки (подзаголовок капсом) — по ФОРМАТУ карточки, раскладка
	// на это не влияет. Плашка «Это ты» к формату больше не привязана: раньше она
	// была зашита в широкий вид и вставала на каждую карточку блока (владелец 16.09),
	// теперь — по флагу карточки you_badge.
	const wideLook = tile === 'wide'
	// Подложка: HEX-цвет (инлайн + автоконтраст текста) > легаси-тинт 'event'
	const bgHex = normHex(bgColor)
	const themeCls = bgHex ? styles.themeCustom : theme === 'event' ? styles.themeEvent : ''
	const themeStyle = bgHex
		? ({ background: bgHex, ...contrastVars(bgHex) } as React.CSSProperties)
		: undefined

	// Клик по плитке-ссылке: обычный — SPA-переход через onPick; с модификатором
	// (⌘/ctrl/shift) и средней кнопкой — нативное поведение <a> (новая вкладка).
	const pickClick = (c: TrendFeedCardData, i: number) => (e: React.MouseEvent) => {
		if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
		e.preventDefault()
		onPick(c, i)
	}

	const renderCard = (c: TrendFeedCardData, i: number) => {
		// Плашка акции: тренд — из этапов, обычная карточка — из своей
		// цены; формула и UTC-проверка те же, по которым спишет бэк.
		const promo = c.pipeline?.enabled
			? trendDiscount(c.pipeline)
			: activeDiscount(c.price_override, c.discount_price, c.discount_until)
		const href = cardHref?.(c)
		const droplet = c.tile_style === 'droplet'
		const shape = mixed ? cardShape(c) : null
		const cardCls = `${styles.card}${shape ? ` ${SHAPE_CLASS[shape]}` : ''}${droplet ? ' glass-droplet-host' : ''}`
		const mstyStyles = mstyStylesOf(c)
		const trendMembers = trendMembersOf(c)
		// Обложка-фолбэк мультистиля (первый стиль) — лёгким превью, как у остальных плиток; оригинал — на ошибку.
		const coverFull = c.background_url || mstyCoverOf(c)
		const cover = c.background_url ? c.background_preview_url || c.background_url : coverFull && sectionPreviewUrl(coverFull)
		const inner = (
			<>
				{cover && (
					<img
						className={styles.media}
						src={cover}
						onError={(e) => {
							// Превью нет/битое → один фолбэк на оригинал (без циклов)
							const img = e.currentTarget
							if (!img.dataset.fbk && coverFull && cover !== coverFull) {
								img.dataset.fbk = '1'
								img.src = coverFull
							}
						}}
						alt=""
						loading="lazy"
						decoding="async"
					/>
				)}
				{c.video_url && (
					/* poster ОБЯЗАТЕЛЕН: видео лежит ПОВЕРХ обложки, и без постера видно
					   ПЕРВЫЙ КАДР видео — но только там, где браузер решил его подгрузить.
					   `preload=metadata` его не гарантирует: десктоп кадр берёт, телефон в
					   эконом-режиме — нет, и там оставалась обложка. Отсюда «на одном
					   телефоне обложка, на другом и на десктопе нет». poster делает картинку
					   одинаковой везде: до ховера — обложка, видео только при play(). */
					<FeedVideo
						src={c.video_url}
						loopSrc={loopOf(c)}
						soundOn={soundCard === soundIdOf(c)}
						onSoundLost={() => soundLost(soundIdOf(c))}
						poster={stillOf(c)}
						overlay={!!c.background_url}
						members={queueMembersOf(c)}
						tileId={c.id}
						onPlayingMember={(id) => setPlayingMember(soundIdOf(c), id)}
					/>
				)}
				{/* Левый верх — ОДИН слот-стек (владелец 2026-08-18): бейджи кладутся
				    в него по приоритету и встают столбиком сами. Раньше каждый был
				    прибит к углу своим top/left, и пары «Это ты + стили», «VIRAL +
				    стили» приходилось разводить ручными --Shift-классами; в reels
				    (бейдж уезжает влево) они и вовсе накладывались. */}
				<span className={styles.tlStack}>
					{c.you_badge && (
						<span className={styles.you}>
							<span aria-hidden="true">👤</span> Это ты
						</span>
					)}
					{c.badge_text && (
						<span className={`${styles.badge} ${BADGE_CLASS[c.badge_style || 'viral'] || styles.badgeViral}`}>
							{c.badge_text}
						</span>
					)}
					{c.card_kind === 'flow' && typeof c.flow?.steps === 'number' && c.flow.steps > 0 && (
						<span className={styles.msty} aria-label={`${c.flow.steps} шагов в мастерской`}>
							{c.flow.steps} {c.flow.steps === 1 ? 'шаг' : c.flow.steps < 5 ? 'шага' : 'шагов'}
						</span>
					)}
					{mstyStyles.length > 0 && (
						<span className={styles.msty} aria-label={`${mstyStyles.length} стилей в карточке`}>
							{mstyStyles.length} {mstyStyles.length === 1 ? 'стиль' : mstyStyles.length < 5 ? 'стиля' : 'стилей'}
						</span>
					)}
					{trendMembers.length > 0 && (
						<span className={styles.msty} aria-label={`${trendsLabel(trendMembers.length)} в группе`}>
							{trendsLabel(trendMembers.length)}
						</span>
					)}
				</span>
				{promo && (
					<span
						className={`${styles.promo} ${wideLook && c.badge_text ? styles.promoShift : ''}`}
						aria-label={`Скидка ${promo.percent}%`}
					>
						−{promo.percent}% · {promo.leftLabel}
					</span>
				)}
				{/* Бар «развернуть + звук» — общий на все витрины (CardMediaBar).
				    Динамик только у роликов: сосед `_loop` без дорожки, звук — это
				    оригинал. */}
				<CardMediaBar
					onExpand={() => openLightbox(c)}
					sound={c.video_url ? { on: soundCard === soundIdOf(c), onToggle: () => toggleSound(soundIdOf(c)) } : undefined}
				/>
				<span className={styles.cap}>
					{mstyStyles.length > 0 && (
						<span className={styles.mstyRow} aria-hidden="true">
							{mstyStyles.map((v) => (
								<PreviewImg key={v.key} className={styles.mstySwatch} src={v.after_url as string} alt="" loading="lazy" decoding="async" draggable={false} />
							))}
							{mstyOpenOf(c).map((v) => (
								v.after_url
									? <PreviewImg key={v.key} className={styles.mstySwatch} src={v.after_url} alt="" loading="lazy" decoding="async" draggable={false} />
									: <span key={v.key} className={`${styles.mstySwatch} ${styles.mstySwatchOpen}`} title={v.label}><PencilIcon size={12} /></span>
							))}
						</span>
					)}
					{trendMembers.length > 0 && (onPickMember ? (
						// Клик по кадру (23.10) — группа сразу с этой версией; к плитке-ссылке событие не всплывает.
						<span className={`${styles.mstyRow} ${styles.mstyRowPick}`} role="group" aria-label="Тренды группы">
							{trendMembers.map((m) => (
								<span
									key={m.id}
									role="button"
									tabIndex={0}
									className={styles.mstySwatchBtn}
									aria-label={`Открыть «${m.title}»`}
									title={m.title}
									onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPickMember(c, m.id, i) }}
									onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onPickMember(c, m.id, i) } }}
								>
									{/* Кадр версии, чей ролик сейчас на плитке, — с ободком (очередь роликов). */}
									{m.src
										? <PreviewImg className={`${styles.mstySwatch}${playingMembers[soundIdOf(c)] === m.id ? ` ${styles.mstySwatchPlaying}` : ''}`} src={m.src} alt="" loading="lazy" decoding="async" draggable={false} />
										: <span className={`${styles.mstySwatch} ${styles.mstySwatchOpen}${playingMembers[soundIdOf(c)] === m.id ? ` ${styles.mstySwatchPlaying}` : ''}`}>{m.title.slice(0, 2)}</span>}
								</span>
							))}
						</span>
					) : (
						<span className={styles.mstyRow} aria-hidden="true">
							{trendMembers.map((m) => (
								m.src
									? <PreviewImg key={m.id} className={`${styles.mstySwatch}${playingMembers[soundIdOf(c)] === m.id ? ` ${styles.mstySwatchPlaying}` : ''}`} src={m.src} alt="" loading="lazy" decoding="async" draggable={false} />
									: <span key={m.id} className={`${styles.mstySwatch} ${styles.mstySwatchOpen}${playingMembers[soundIdOf(c)] === m.id ? ` ${styles.mstySwatchPlaying}` : ''}`} title={m.title}>{m.title.slice(0, 2)}</span>
							))}
						</span>
					))}
					<span className={styles.capTitle}>{c.title}</span>
					{/* Карточка-флоу: иконка «Флоу» и цена всего результата под названием —
					    тот же кастом, что у сетки и галерейной плитки (владелец 05.09). */}
					{c.card_kind === 'flow' && (
						<span className={styles.flowMeta}>
							<FlowLinkIcon size={14} className={styles.flowMetaIcon} />
							Мастерская
							{c.flow?.price_from != null && (
								<span className={styles.flowPrice}>· от {c.flow.price_from} <Coin width={11} height={11} /></span>
							)}
						</span>
					)}
					{!hoverAction && (wideLook || shape === 'wide') && c.description && <span className={styles.capSub}>{c.description}</span>}
					{/* Две обёртки: внешняя анимирует высоту ряда (0fr → 1fr), внутренняя режет содержимое — с
					    min-height на самом тексте переход высоты не стартует (грабли карточки «Популярных»). */}
					{hoverAction && (
						<span className={styles.hoverWrap} aria-hidden="true">
							<span className={styles.hoverClip}>
								{c.description && <span className={styles.hoverText}>{c.description}</span>}
								</span>
						</span>
					)}
				</span>
				{/* Действие плитки — угловой стрелкой, как в витрине главной: один знак «перейти» на
				    весь сайт. Кнопка внутри плитки-ссылки была декорацией (aria-hidden) и закрывала кадр. */}
				{hoverAction && <span className={styles.go} aria-hidden="true" />}
				{droplet && <DropletGlass />}
			</>
		)
		return href ? (
			<a
				key={c.id || i}
				href={href}
				className={cardCls}
				onClick={pickClick(c, i)}
				onMouseEnter={playOn}
				onMouseLeave={playOff}
				aria-label={c.title || 'Тренд'}
			>{inner}</a>
		) : (
			<button
				key={c.id || i}
				type="button"
				className={cardCls}
				onClick={() => onPick(c, i)}
				onMouseEnter={playOn}
				onMouseLeave={playOff}
				aria-label={c.title || 'Тренд'}
			>{inner}</button>
		)
	}

	// ── Hero: одна крупная карточка «Тренд недели» с CTA (медиа 3:4,
	// формат карточек на него не влияет) ──
	if (arrangement === 'hero') {
		const hc = heroCard ?? cards[0]
		if (!hc) return null
		const promo = hc.pipeline?.enabled
			? trendDiscount(hc.pipeline)
			: activeDiscount(hc.price_override, hc.discount_price, hc.discount_until)
		const heroHref = cardHref?.(hc)
		const heroDroplet = hc.tile_style === 'droplet'
		const heroCls = `${styles.card}${heroDroplet ? ' glass-droplet-host' : ''}`
		// Hero берёт ОРИГИНАЛ обложки (см. <img> ниже), поэтому свой still.
		const heroStill = hc.background_url || hc.video_preview_url || undefined
		const heroMedia = (
			<>
				{hc.background_url && (
					/* eager: hero — первый экран раздела и главная картинка страницы,
					   с lazy браузер откладывал её и тянул LCP. Оригинал, а НЕ лёгкое
					   превью: hero рисуется 300×400 CSS (600×800 на 2x), а превью ≤512px
					   по длинной стороне — его бы растянуло и замылило. */
					<img className={styles.media} src={hc.background_url} alt="" loading="eager" decoding="async" fetchPriority="high" />
				)}
				{hc.video_url && (
					<FeedVideo
						src={hc.video_url}
						loopSrc={hc.video_loop_url || undefined}
						poster={heroStill}
						overlay={!!hc.background_url}
						soundOn={soundCard === soundIdOf(hc)}
						onSoundLost={() => soundLost(soundIdOf(hc))}
						members={queueMembersOf(hc)}
						tileId={hc.id}
					/>
				)}
				{heroDroplet && <DropletGlass />}
				{/* Тот же бар, что на плитках (владелец 15.09: «на главный тренд тоже
				    добавить пункты управления»); у героя виден всегда — см. модуль стилей. */}
				<CardMediaBar
					onExpand={() => openLightbox(hc)}
					sound={hc.video_url ? { on: soundCard === soundIdOf(hc), onToggle: () => toggleSound(soundIdOf(hc)) } : undefined}
				/>
			</>
		)
		return (
			<section className={`${styles.feed} ${styles.feedHero} ${themeCls}${fullWidth ? ` ${styles.feedFull}` : ''}`} style={themeStyle}>
				<div className={styles.heroCard}>
					{heroHref ? (
						<a
							href={heroHref}
							className={heroCls}
							onClick={pickClick(hc, 0)}
							onMouseEnter={playOn}
							onMouseLeave={playOff}
							aria-label={hc.title || 'Тренд'}
						>{heroMedia}</a>
					) : (
						<button
							type="button"
							className={heroCls}
							onClick={() => onPick(hc, 0)}
							onMouseEnter={playOn}
							onMouseLeave={playOff}
							aria-label={hc.title || 'Тренд'}
						>{heroMedia}</button>
					)}
					<div className={styles.heroBody}>
						<div className={styles.heroKicker}>
							{/* Пустая иконка = иконки нет. Огонёк был подстановкой в коде, и
							    очистить поле в админке было нельзя (владелец 14.09). */}
							{icon && <span aria-hidden="true">{icon}</span>} {title}
						</div>
						<h2 className={styles.heroTitle}>{hc.title}</h2>
						{(hc.description || subtitle) && (
							<p className={styles.heroDesc}>{hc.description || subtitle}</p>
						)}
						<div className={styles.heroMeta}>
							{hc.badge_text && (
								<span className={`${styles.heroChip} ${BADGE_CLASS[hc.badge_style || 'viral'] || styles.badgeViral}`}>
									{hc.badge_text}
								</span>
							)}
							{promo && (
								<span className={styles.heroChip} aria-label={`Скидка ${promo.percent}%`}>
									−{promo.percent}% · {promo.leftLabel}
								</span>
							)}
						</div>
						<button type="button" className={styles.heroCta} onClick={() => onPick(hc, 0)}>
							Повторить тренд →
						</button>
					</div>
				</div>
				{/* Лайтбокс монтируется и у героя: ветка возвращается раньше общей
				    разметки, и без этого «развернуть» на герое молча ничего не открывал. */}
				{lightboxImages && (
					<CardImageLightbox images={lightboxImages} onClose={() => setLightboxImages(null)} />
				)}
			</section>
		)
	}

	// ── grid: первый экран — целые ряды, хвост — под аккордеоном ──
	// Ряд считаем по РЕАЛЬНОМУ числу колонок (auto-fill зависит от ширины), иначе
	// ряд обрывается посередине. То же правило у блоков разделов (BlockGrid).
	const isGrid = arrangement === 'grid'
	const limit = isGrid
		? gridCols > 0
			? (visibleRows > 0 ? visibleRows : Math.max(1, Math.floor(visibleCount / gridCols))) * gridCols
			: Math.max(1, visibleRows > 0 ? visibleRows * 4 : visibleCount)
		: cards.length
	const shown = isGrid ? cards.slice(0, limit) : cards
	const rest = isGrid ? cards.slice(limit) : []

	return (
		<section
			className={`${styles.feed} ${TILE_CLASS[tile]} ${ARR_CLASS[arrangement]} ${sizeCls} ${themeCls}${hoverAction ? ` ${styles.hoverAction}` : ''}${fullWidth ? ` ${styles.feedFull}` : ''}`}
			style={fitCols ? ({ ...themeStyle, '--tf-grid-cols': fitCols } as React.CSSProperties) : themeStyle}
		>
			<div className={styles.head}>
				{icon && <div className={styles.icon}>{icon}</div>}
				<div>
					<h2 className={styles.title}>{title}</h2>
					{subtitle && <div className={styles.subtitle}>{subtitle}</div>}
				</div>
			</div>
			{/* Раскладка «слайдер» — та же полка, что у блоков разделов: мягкие края и
			    кнопки листания даёт общий SliderRow (владелец 14.09: «принцип блоков
			    используется и в трендах, его давно следует доработать»). Размеры плиток
			    и зазор остаются за модулем ленты — SliderRow их не трогает. */}
			{arrangement === 'slider' ? (
				<SliderRow rowClassName={styles.row} label={title}>
					{shown.map((c, i) => renderCard(c, i))}
				</SliderRow>
			) : (
				<div className={styles.row} ref={rowRef}>
					{shown.map((c, i) => renderCard(c, i))}
				</div>
			)}
			{rest.length > 0 && (
				<>
					<div className={`${styles.moreWrap} ${expanded ? styles.moreOpen : ''}`}>
						<div className={styles.moreInner}>
							{/* aria-hidden в свёрнутом виде: плитки не в табе и не в ридере.
							    tailMounted: хвост не тянет сеть, пока не раскрыли. */}
							<div className={styles.row} aria-hidden={!expanded}>
								{tailMounted && rest.map((c, i) => renderCard(c, i + shown.length))}
							</div>
						</div>
					</div>
					<button
						type="button"
						className={styles.moreBtn}
						aria-expanded={expanded}
						onClick={() => {
							setTailMounted(true)
							setExpanded((v) => !v)
						}}
					>
						{expanded ? 'Свернуть' : `Показать другие тренды (${rest.length})`}
					</button>
				</>
			)}

			{lightboxImages && (
				<CardImageLightbox images={lightboxImages} onClose={() => setLightboxImages(null)} />
			)}
		</section>
	)
}
